import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Components
import ModalContainer from '../../containers/ModalContainer';

// Styled Components
import {
  Container,
  FormContainer,
  InputContainer,
  StyledInputText,
  InfoText,
  ButtonContainer,
  CancelButton,
  SubmitButton,
} from './styles';

// Types
import { ModalProps, ChangePasswordFormType } from './types';

// Schema
import { ChangePasswordFormSchema } from './schema';

// GraphQL
import { client } from '../../../../gql/client';
import { UPDATE_PASSWORD } from '../../../../gql/mutations/auth';

// Sentry
import * as Sentry from '@sentry/react';

// Media
import { useIsLandscapeTablet } from '../../../../hooks/useMediaBreakPoints';

// Utils
import { showSuccessToast, showErrorToast } from '../../../../utils/toasts';

export const ChangePasswordModal = ({
  onClickClose,
  ...props
}: ModalProps): JSX.Element => {
  const { t } = useTranslation('auth');
  const isLandscapeTablet = useIsLandscapeTablet();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<ChangePasswordFormType>({
    mode: 'onTouched',
    reValidateMode: 'onBlur',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      repeatPassword: '',
    },
    resolver: yupResolver(ChangePasswordFormSchema) as any,
  });

  const onSubmit = async (data: ChangePasswordFormType) => {
    setIsSubmitting(true);

    try {
      await client.mutate({
        mutation: UPDATE_PASSWORD,
        variables: {
          input: {
            currentPassword: data.currentPassword,
            newPassword: data.newPassword,
            // No incluir userId para que el usuario cambie su propia contraseña
          },
        },
      });

      // Mostrar toast de éxito
      showSuccessToast(t('changePassword.successMessage'));

      // Limpiar formulario y cerrar modal
      reset();
      onClickClose();
    } catch (err) {
      Sentry.captureException(err);
      console.log('Error', err);

      const errorMessage = (err as Error)?.message;

      // Mostrar toast de error específico
      if (errorMessage?.includes('Invalid current password')) {
        showErrorToast(t('changePassword.invalidCurrentPassword'));
      } else if (
        errorMessage?.includes('Password does not meet requirements')
      ) {
        showErrorToast(t('changePassword.passwordRequirements'));
      } else {
        showErrorToast(t('changePassword.errorMessage'));
      }

      // El modal NO se cierra en caso de error
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalContainer
      width={50}
      onClickClose={onClickClose}
      fill={theme.colors.black}
      title={t('changePassword.title')}
    >
      <Container {...props}>
        <FormContainer onSubmit={handleSubmit(onSubmit)}>
          <InputContainer>
            <StyledInputText
              {...register('currentPassword')}
              label={t('changePassword.currentPassword')}
              labelSize={isLandscapeTablet ? 'XS' : 'S'}
              typeInput="password"
              type="password"
              placeholder={t('changePassword.currentPassword')}
              autoComplete="current-password"
              error={errors.currentPassword?.message}
            />

            <StyledInputText
              {...register('newPassword')}
              label={t('changePassword.newPassword')}
              labelSize={isLandscapeTablet ? 'XS' : 'S'}
              typeInput="password"
              type="password"
              placeholder={t('changePassword.newPassword')}
              autoComplete="new-password"
              error={errors.newPassword?.message}
            />

            <InfoText size="XS">
              {t('changePassword.passwordInstructions')}
            </InfoText>

            <StyledInputText
              {...register('repeatPassword')}
              label={t('changePassword.confirmPassword')}
              labelSize={isLandscapeTablet ? 'XS' : 'S'}
              typeInput="password"
              type="password"
              placeholder={t('changePassword.confirmPassword')}
              autoComplete="new-password"
              error={errors.repeatPassword?.message}
            />
          </InputContainer>

          <ButtonContainer>
            <CancelButton
              type="button"
              text={t('common:buttons.cancel')}
              variant="secondary"
              onClick={onClickClose}
              disabled={isSubmitting}
            />
            <SubmitButton
              type="submit"
              text={
                isSubmitting
                  ? t('changePassword.changing')
                  : t('changePassword.changeButton')
              }
              disabled={isSubmitting}
            />
          </ButtonContainer>
        </FormContainer>
      </Container>
    </ModalContainer>
  );
};
